import HighchartsReact from 'highcharts-react-official';
import Highcharts, {
  DataLabelsOptions,
  SeriesOptionsType,
  merge,
} from 'highcharts';
import React, { useRef, useState, useMemo, useEffect } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';

import { ChartProps } from './AssetTransitionChart';
import { unitFormat } from 'components/features/new-report/utils/constructor';
import { commaFormat } from 'components/features/new-report/utils/formatter';
import { marker } from 'components/features/utils/chartUtils';
import {
  ExtraTimeSeriesData,
  TimeSeriesDatum,
} from 'components/features/home/assetStuffing/AssetStuffing';
import { IconOnChart } from 'components/features/home/assetStuffing/AssetStuffingChart';
import { formatDateForString } from 'utils/formatter';

const colorTypes = {
  国内株式: '#179a8d',
  信用取引: '#7ad8c6',
  外国株式: '#3fa9f5',
  FX: '#5458c6',
  仮想通貨: '#f2c30b',
  投資信託: '#dd80d4',
  預金: '#9f3bf4',
  外貨預金: '#df82d2',
  その他: '#d2deef',
  ロボアド: '#f47f37',
  未上場株式: '#8C8C8C',
  '国内ETF・ETN': '#6D71DA',
  '外国ETF・ETN': '#17CECE',
  国内REIT: '#FE5F5F',
  外国REIT: '#D09494',
  国内債券: '#26E016',
  外国債券: '#F0C22B',
  国内EB: '#4098B4',
  外国EB: '#91BEAB',
  不動産: '#FF58DA',
} as const;

export const Chart: React.FC<ChartProps> = ({
  chartData,
  periodVerbose,
  xAxisInfo,
  dateFormat,
}) => {
  const [icons, setIcons] = useState<IconOnChart[]>([]);
  const [toolTipPosition, setToolTipPosition] = useState<{
    x: number;
    y: number;
    width: number;
    date: Date | null;
  }>({ x: 0, y: 0, width: 0, date: null });
  const [showToolTip, setShowToolTip] = useState<boolean>(false);
  const extraData = useMemo(() => {
    const incorrectSign: ExtraTimeSeriesData = {
      type: 'IncorrectSign',
      value: chartData.map(it => {
        return { value: it.Incorrect, date: it.date } as TimeSeriesDatum;
      }),
    };
    return [incorrectSign];
  }, [chartData]);

  const getArrangeChartData = (
    data: ChartProps['chartData'],
    periodVerbose: string
  ): SeriesOptionsType[] => {
    const ret: SeriesOptionsType[][] = [
      [
        {
          type: 'line',
          name: `${periodVerbose}`,
          color: '#ef4444',
          data: (data.map(r => [
            `${periodVerbose}末資産総額`,
            r.prevTotalAssetAmount,
          ]) as [string, number][]).map((v, i, arr) => {
            if (i === arr.length - 1) {
              const ret: {
                y: number;
                name: string;
                dataLabels: DataLabelsOptions;
              } = {
                y: v[1],
                name: v[0],
                dataLabels: {
                  enabled: true,
                  formatter: function(_): string {
                    return periodVerbose;
                  },
                  className: 'stick-out-data-label asset-transition',
                  useHTML: true,
                  padding: 0,
                  crop: false,
                  color: 'white',
                  allowOverlap: false,
                },
              };
              return ret;
            }
            return v;
          }),
          marker: { enabled: false },
          legendIndex: 1,
        },
      ],
      isShowSeries(data.map(r => r.Stock))
        ? [
            {
              type: 'area',
              name: '国内株式',
              color: colorTypes.国内株式,
              data: data.map(r => ['国内株式', r.Stock]),
              legendIndex: 2,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.UnlistedStockAmount))
        ? [
            {
              type: 'area',
              name: '未上場株式',
              color: colorTypes.未上場株式,
              data: data.map(r => ['未上場株式', r.UnlistedStockAmount]),
              legendIndex: 3,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.InvestmentTrust))
        ? [
            {
              type: 'area',
              name: '投資信託',
              color: colorTypes.投資信託,
              data: data.map(r => ['投資信託', r.InvestmentTrust]),
              legendIndex: 4,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.ForeignStock))
        ? [
            {
              type: 'area',
              name: '外国株式',
              color: colorTypes.外国株式,
              data: data.map(r => ['外国株式', r.ForeignStock]),
              legendIndex: 5,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.EtfEtnAmount))
        ? [
            {
              type: 'area',
              name: '国内ETF・ETN',
              color: colorTypes['国内ETF・ETN'],
              data: data.map(r => ['国内ETF・ETN', r.EtfEtnAmount]),
              legendIndex: 6,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.ForeignEtfEtnAmount))
        ? [
            {
              type: 'area',
              name: '外国ETF・ETN',
              color: colorTypes['外国ETF・ETN'],
              data: data.map(r => ['外国ETF・ETN', r.ForeignEtfEtnAmount]),
              legendIndex: 7,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.ReitAmount))
        ? [
            {
              type: 'area',
              name: '国内REIT',
              color: colorTypes.国内REIT,
              data: data.map(r => ['国内REIT', r.ReitAmount]),
              legendIndex: 8,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.ForeignReitAmount))
        ? [
            {
              type: 'area',
              name: '外国REIT',
              color: colorTypes.外国REIT,
              data: data.map(r => ['外国REIT', r.ForeignReitAmount]),
              legendIndex: 9,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.BondAmount))
        ? [
            {
              type: 'area',
              name: '国内債券',
              color: colorTypes.国内債券,
              data: data.map(r => ['国内債券', r.BondAmount]),
              legendIndex: 10,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.ForeignBondAmount))
        ? [
            {
              type: 'area',
              name: '外国債券',
              color: colorTypes.外国債券,
              data: data.map(r => ['外国債券', r.ForeignBondAmount]),
              legendIndex: 11,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.EbAmount))
        ? [
            {
              type: 'area',
              name: '国内EB',
              color: colorTypes.国内EB,
              data: data.map(r => ['国内EB', r.EbAmount]),
              legendIndex: 12,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.ForeignEbAmount))
        ? [
            {
              type: 'area',
              name: '外国EB',
              color: colorTypes.外国EB,
              data: data.map(r => ['外国EB', r.ForeignEbAmount]),
              legendIndex: 13,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.RealEstateAmount))
        ? [
            {
              type: 'area',
              name: '不動産',
              color: colorTypes.不動産,
              data: data.map(r => ['不動産', r.RealEstateAmount]),
              legendIndex: 14,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.RoboAdvisor))
        ? [
            {
              type: 'area',
              name: 'ロボアド',
              color: colorTypes.ロボアド,
              data: data.map(r => ['ロボアド', r.RoboAdvisor]),
              legendIndex: 15,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.MarginProfitOrLoss))
        ? [
            {
              type: 'area',
              name: '信用取引',
              color: colorTypes.信用取引,
              data: data.map(r => ['信用取引', r.MarginProfitOrLoss]),
              legendIndex: 16,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.Cash))
        ? [
            {
              type: 'area',
              name: '預金・MRF',
              color: colorTypes.預金,
              data: data.map(r => ['預金・MRF', r.Cash]),
              legendIndex: 17,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
      isShowSeries(data.map(r => r.Other))
        ? [
            {
              type: 'area',
              name: 'その他',
              color: colorTypes.その他,
              data: data.map(r => ['その他', r.Other]),
              legendIndex: 18,
              marker: { enabled: false },
              fillOpacity: 0.6,
            },
          ]
        : [],
    ];
    return ret.flat();
  };

  const getXAxis = () => {
    let categories = [] as Highcharts.XAxisOptions;
    let labels = [] as Highcharts.XAxisOptions;

    if (dateFormat === 'yyyy/MM') {
      categories = {
        categories: chartData.map(r =>
          ((date): string => {
            return `${date.getFullYear()}/${date.getMonth() +
              1}/${date.getDate()}`;
          })(r.date)
        ),
      };
      labels = {
        labels: {
          formatter: function(): string {
            return `${formatDateForString(this.value.toString(), 'yyyy/M')}`;
          },
        },
      };
    } else {
      categories = {
        categories: chartData.map(r =>
          ((date, dateFormat): string => {
            const dateString = `${date.getMonth() + 1}/${date.getDate()}`;
            const day = ['日', '月', '火', '水', '木', '金', '土'][
              date.getDay()
            ];
            if (dateFormat === 'M/d\nE')
              return `<span class="tooltip-date-label"><span class="date">${dateString}</span><br/><span class="day">${day}</span></span>`;
            return dateString;
          })(r.date, dateFormat)
        ),
      };
    }

    return {
      ...xAxisInfo,
      ...categories,
      ...labels,
      crosshair: {
        snap: true,
        width: 1,
        color: '#777',
      },
      angle: 0,
    };
  };

  const [options, setOptions] = useState<Highcharts.Options>({
    credits: {
      enabled: false,
    },
    xAxis: getXAxis(),
    yAxis: {
      opposite: true,
      title: { text: '(万円)', align: 'high', offset: 0, rotation: 0, y: -15 },
      labels: {
        padding: 0,
        x: 0,
        formatter: function(): string {
          return typeof this.value === 'number'
            ? `${commaFormat(Math.round(this.value / 10000), {
                numberOfDecimals: 0,
              })}`
            : '';
        },
      },
    },
    subtitle: {
      text: '',
    },
    title: {
      text: '',
    },
    tooltip: {
      borderColor: 'transparent',
      useHTML: true,
      outside: true,
      headerFormat: renderToStaticMarkup(
        <span className="tooltip-header">{'{point.x}'}</span>
      ),
      pointFormatter: function(): string {
        const Tooltip: React.FC<{
          name: string;
          color: string;
          value: number;
        }> = ({ name, color, value }) => {
          return (
            <div className="tooltip">
              <div
                className="tooltip-point"
                style={{ backgroundColor: color }}
              />
              <span className="tooltip-label">{name}:</span>
              <span className="tooltip-value">{unitFormat(value, 2)}円</span>
            </div>
          );
        };
        return renderToStaticMarkup(
          <Tooltip
            name={this.name}
            color={this.color?.toString() ?? ''}
            value={this.y ?? 0}
          />
        );
      },
      shared: true,
    },
    plotOptions: {
      series: { ...marker, turboThreshold: 15000 },
      area: {
        stacking: 'normal',
        events: {
          legendItemClick: () => false,
        },
        turboThreshold: 15000,
      },
      line: {
        events: {
          legendItemClick: () => false,
        },
        turboThreshold: 15000,
      },
    },
    exporting: {
      enabled: false,
    },
    legend: {
      itemDistance: -10,
      verticalAlign: 'top',
      margin: 15,
      itemStyle: {
        fontWeight: 'normal',
      },
      maxHeight: 55,
      itemWidth: 110,
      navigation: {
        activeColor: '#fdba74',
        arrowSize: 12,
      },
      useHTML: true,
      className: 'override-highcharts-legend common-margin-top',
      labelFormatter: function() {
        if (this instanceof Highcharts.Series) {
          const name = this.name;
          const color = (() => {
            const t = this as unknown;
            if (
              typeof t === 'object' &&
              t &&
              'color' in t &&
              typeof (t as { color: unknown }).color === 'string'
            ) {
              return (t as { color: string }).color;
            }
            return '';
          })();
          if (this.type === 'area') {
            return renderToStaticMarkup(
              <AreaChartLegend color={color} lightOpacity={0.6}>
                {name}
              </AreaChartLegend>
            );
          }
          if (this.type === 'line') {
            return renderToStaticMarkup(
              <LineChartLegend color={color}>{name}</LineChartLegend>
            );
          }
        }
        return '';
      },
    },
  });

  useEffect(() => {
    setOptions(prev => {
      return merge({
        ...prev,
        chart: {
          height: 330,
          events: {
            redraw: function() {
              if (ref.current) {
                const chart = ref.current.chart;
                drawIcons({ chart, icons, extraData });
              }
            },
          },
        },
        series: getArrangeChartData(chartData, periodVerbose),
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartData]);

  const drawIcons = ({
    chart,
    extraData,
  }: {
    chart: Highcharts.Chart;
    icons: IconOnChart[];
    extraData: ExtraTimeSeriesData[];
  }): void => {
    const iconSize = 15;
    const margin = 5;
    icons.forEach(({ icon }) => {
      if (icon.renderer) icon.destroy();
    });
    const xAxis = chart.xAxis[0];

    const incorrect = extraData.find(it => it.type === 'IncorrectSign');
    if (!incorrect) return;

    incorrect.value.forEach(({ date, value }, index) => {
      if (!date || !value) return;
      const x = xAxis.toPixels(index, true);
      if (!chart.isInsidePlot(x, chart.plotTop)) return;
      const icon = chart.renderer
        .image(
          '/static/images/icon/icon-info.svg',
          x,
          chart.plotTop + chart.plotHeight - 7,
          iconSize,
          iconSize
        )
        .add();
      icon.toFront();

      const tooltipWidth = 150;
      let tx = x - tooltipWidth - margin;
      if (!chart.isInsidePlot(tx, chart.plotTop)) {
        tx = x + iconSize + margin;
      }

      icon.element.addEventListener('mouseover', () => {
        setToolTipPosition({
          x: tx,
          y: chart.chartHeight - chart.plotTop - chart.plotHeight,
          width: tooltipWidth,
          date: date,
        });
        setShowToolTip(true);
      });
      icon.element.addEventListener('mouseout', () => {
        setShowToolTip(false);
      });

      icons.push({ icon });
      setIcons(icons);
    });
  };

  const Tip = ({
    pos,
    show,
  }: {
    pos: { x: number; y: number; width: number; date: Date | null };
    show: boolean;
  }) => {
    return (
      <div
        className="p-2 absolute bg-white rounded-md shadow-md text-xs w-1/2 border border-gray-300"
        style={{
          display: show ? 'block' : 'none',
          zIndex: 999,
          left: pos.x,
          bottom: pos.y,
          maxWidth: pos.width,
        }}>
        <div className="flex flex-row">
          <div
            className="icon-info text-sm"
            id="icon-info"
            style={{ color: '#94A3B8' }}></div>
          <div className="ml-1 text-1.5xs">
            <div>
              集計が正常に完了しなかった可能性があるためこのデータは参考値です。
            </div>
            <div className="mt-1">
              {pos.date !== null
                ? `${pos.date.getFullYear()}/${pos.date.getMonth() +
                    1}/${pos.date.getDate()}`
                : '----/--/--'}
            </div>
          </div>
        </div>
      </div>
    );
  };
  const ref = useRef<HighchartsReact.RefObject>(null);

  return (
    <div className="relative">
      <HighchartsReact highcharts={Highcharts} options={options} ref={ref} />
      <Tip pos={toolTipPosition} show={showToolTip} />
    </div>
  );
};

const isShowSeries = (values: (number | null | undefined)[]): boolean => {
  return !values.every(v => v === 0 || v === null || v === undefined);
};

type ChartLegendProps = {
  color: string;
  children: React.ReactNode;
};

type AreaChartLegendProps = ChartLegendProps & { lightOpacity: number };

export const AreaChartLegend = ({
  color,
  children,
  lightOpacity,
}: AreaChartLegendProps) => {
  return (
    <>
      <div className="highcharts-legend-symbol-area">
        <div
          style={{ backgroundColor: color }}
          className="highcharts-legend-symbol-area-dark"></div>
        <div
          style={{ backgroundColor: color, opacity: lightOpacity }}
          className="highcharts-legend-symbol-area-light"></div>
      </div>
      {children}
    </>
  );
};

export const LineChartLegend = ({ color, children }: ChartLegendProps) => {
  return (
    <>
      <div
        className="highcharts-legend-symbol-line"
        style={{ backgroundColor: color }}></div>
      {children}
    </>
  );
};
